// store/modalStore.ts
import { create } from 'zustand';
import { MODAL_IDS, type ModalId } from '@/utils/modal-registry';

/**
 * Single-slot modal state.
 *
 * At most one same-page modal is open at a time, which is what makes the
 * "many event sources, one renderer" flow race-free: every source dispatches
 * the same action with the same id, so there is nothing to contend over, and
 * opening anything replaces the slot atomically.
 */
interface ModalState {
  /** The modal currently requested, or `null` when none is. */
  activeModal: ModalId | null;
  /**
   * Defaults to Get Started so every existing `openModal()` call site keeps
   * its exact current meaning.
   */
  openModal: (id?: ModalId) => void;
  /**
   * Deliberately zero-arg. Several call sites pass this straight to `onClick`
   * (`Popup`, `Modal`, both navbars), so any parameter here would silently
   * receive a `MouseEvent`.
   */
  closeModal: () => void;
}

export const useModalStore = create<ModalState>((set) => ({
  activeModal: null,
  openModal: (id = MODAL_IDS.getStarted) =>
    // Re-opening the modal that is already open is a true no-op: zustand bails
    // on `Object.is` before notifying, so no subscriber runs and the enter
    // animation is never restarted.
    set((state) => (state.activeModal === id ? state : { activeModal: id })),
  closeModal: () => set({ activeModal: null }),
}));

/**
 * Subscribe to exactly one modal's open state.
 *
 * Returns a boolean rather than an object so the selector stays referentially
 * stable — zustand v5 no longer caches selector results by equality, and a
 * selector returning a fresh object would re-render forever.
 */
export const useIsModalOpen = (id: ModalId = MODAL_IDS.getStarted) =>
  useModalStore((state) => state.activeModal === id);
